/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { Fragment, memo, Suspense, useMemo } from 'react';

import { keyframes, cssMap as unboundCssMap } from '@compiled/react';

import { cssMap, jsx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { type RendererProps } from '@atlaskit/renderer';
import { encodeAdf } from '@atlassian/conversation-assistant-content-transformer/adf-encoder';

import {
	StatusCitation,
	type StatusCitationProps,
} from '../../../ai-answer-dialog/ai-answer-body/ai-answer-result/rendered/status-citation';

const AkReactRenderer = React.lazy(() =>
	import(/* webpackChunkName: "@atlaskit-internal_.renderer" */ '@atlaskit/renderer').then(
		({ ReactRenderer }) => {
			return {
				default: ReactRenderer,
			};
		},
	),
);

const fadeIn = keyframes({
	from: {
		opacity: 0,
	},
	to: {
		opacity: 1,
	},
});

const animatedStyles = cssMap({
	container: {
		animation: `${fadeIn} 700ms cubic-bezier(0.3,0,0.8,0.15) forwards`,
	},
});

const unboundAnimatedStyles = unboundCssMap({
	container: {
		opacity: 0,
	},
	listItem: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > li::marker': {
			opacity: 0,
			animation: `${fadeIn} 700ms cubic-bezier(0.3,0,0.8,0.15) forwards`,
		},
	},
});

const StaggeredChild = memo(({ children, index }: { children: React.ReactNode; index: number }) => {
	// If children is a string longer than 10 characters, split it into 3 parts
	if (typeof children === 'string' && children.length > 10) {
		const chunkSize = Math.ceil(children.length / 3);
		const parts = [
			children.slice(0, chunkSize),
			children.slice(chunkSize, chunkSize * 2),
			children.slice(chunkSize * 2),
		];

		return (
			<Fragment>
				{parts.map((part, partIndex) => (
					<span
						key={partIndex}
						css={[animatedStyles.container, unboundAnimatedStyles.container]}
						style={{
							animationDelay: `${index * 200 + partIndex * 100}ms`,
						}}
					>
						{part}
					</span>
				))}
			</Fragment>
		);
	}

	// For non-strings or short strings, render normally
	return (
		<span
			css={[animatedStyles.container, unboundAnimatedStyles.container]}
			style={{
				animationDelay: `${index * 200}ms`,
			}}
		>
			{children}
		</span>
	);
});

StaggeredChild.displayName = 'StaggeredChild';

const StaggeredAnimatedParagraph = memo(({ children }: { children: React.ReactNode }) => {
	// Convert children to array to handle staggering
	const childrenArray = React.Children.toArray(children);

	return (
		<Box>
			{childrenArray.map((child, index) => (
				<StaggeredChild key={index} index={index}>
					{child}
				</StaggeredChild>
			))}
		</Box>
	);
});

StaggeredAnimatedParagraph.displayName = 'StaggeredAnimatedParagraph';

const AnimatedOrderedList = memo(
	({ children, ...props }: { children: React.ReactNode; [key: string]: any }) => {
		return (
			<ol
				{...props}
				css={[
					animatedStyles.container,
					unboundAnimatedStyles.container,
					unboundAnimatedStyles.listItem,
				]}
			>
				{children}
			</ol>
		);
	},
);

AnimatedOrderedList.displayName = 'AnimatedOrderedList';

const AnimatedBulletedList = memo(
	({ children, ...props }: { children: React.ReactNode; [key: string]: any }) => {
		return (
			<ul
				{...props}
				css={[
					animatedStyles.container,
					unboundAnimatedStyles.container,
					unboundAnimatedStyles.listItem,
				]}
			>
				{children}
			</ul>
		);
	},
);

AnimatedBulletedList.displayName = 'AnimatedBulletedList';

const AnimatedHeading = memo(
	({ children, ...props }: { children: React.ReactNode; [key: string]: any }) => {
		// Extract heading level from props (h1, h2, h3, etc.)
		const headingLevel = props.level || 1;
		const HeadingTag = `h${headingLevel}` as keyof JSX.IntrinsicElements;

		return (
			<HeadingTag {...props} css={[animatedStyles.container, unboundAnimatedStyles.container]}>
				{children}
			</HeadingTag>
		);
	},
);

AnimatedHeading.displayName = 'AnimatedHeading';

// Create stable component references to prevent recreation
const StatusComponent = memo((props: StatusCitationProps) => <StatusCitation {...props} />);
StatusComponent.displayName = 'StatusComponent';

const MediaComponent = memo(() => <Fragment />);
MediaComponent.displayName = 'MediaComponent';

const MediaSingleComponent = memo(() => <Fragment />);
MediaSingleComponent.displayName = 'MediaSingleComponent';

// Create stable component references
const StreamingParagraphComponent = memo((props: any) => <StaggeredAnimatedParagraph {...props} />);
StreamingParagraphComponent.displayName = 'StreamingParagraphComponent';

const StreamingOrderedListComponent = memo((props: any) => <AnimatedOrderedList {...props} />);
StreamingOrderedListComponent.displayName = 'StreamingOrderedListComponent';

const StreamingBulletedListComponent = memo((props: any) => <AnimatedBulletedList {...props} />);
StreamingBulletedListComponent.displayName = 'StreamingBulletedListComponent';

const StreamingHeadingComponent = memo((props: any) => <AnimatedHeading {...props} />);
StreamingHeadingComponent.displayName = 'StreamingHeadingComponent';

// Create stable nodeComponents objects
const staticNodeComponents = {
	status: StatusComponent,
	media: MediaComponent,
	mediaSingle: MediaSingleComponent,
};

const animatedNodeComponents = {
	status: StatusComponent,
	media: MediaComponent,
	mediaSingle: MediaSingleComponent,
	paragraph: StreamingParagraphComponent,
	orderedList: StreamingOrderedListComponent,
	bulletList: StreamingBulletedListComponent,
	heading: StreamingHeadingComponent,
};

export function ReactRenderer({
	markdown,
	animateComponents = false,
}: {
	markdown: string;
	animateComponents?: boolean;
}): JSX.Element {
	const docContent = useMemo(() => encodeAdf(markdown, true), [markdown]);
	const enableImageSearch = fg('hybrid_orchestrator_enable_image_search');

	const config: RendererProps = useMemo(() => {
		const nodeComponents = animateComponents
			? { ...animatedNodeComponents }
			: { ...staticNodeComponents };
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const { media, mediaSingle, ...nodeComponentsWithoutMediaOverrides } = nodeComponents;

		return {
			document: docContent,
			nodeComponents: enableImageSearch ? nodeComponentsWithoutMediaOverrides : nodeComponents,
			smartLinks: {
				ssr: true,
			},
		};
	}, [docContent, animateComponents, enableImageSearch]);

	return (
		<Suspense fallback={<Fragment />}>
			<AkReactRenderer {...config} />
		</Suspense>
	);
}
